import { useState } from 'react';
import type { RpCustomSection } from '../../stores/playSessionStore';
import { mergeWithDefaults, BUILT_IN_SECTION_LABELS } from './rpSectionUtils';

interface RpCustomizePanelProps {
  sectionOrder: string[];
  hiddenSections: string[];
  customSections: RpCustomSection[];
  onMove: (sectionId: string, direction: 'up' | 'down') => void;
  onToggleVisibility: (sectionId: string) => void;
  onAddCustomSection: (title: string) => void;
  onRemoveCustomSection: (id: string) => void;
  onRenameCustomSection: (id: string, title: string) => void;
  onClose: () => void;
}

export function RpCustomizePanel({
  sectionOrder,
  hiddenSections,
  customSections,
  onMove,
  onToggleVisibility,
  onAddCustomSection,
  onRemoveCustomSection,
  onRenameCustomSection,
  onClose,
}: RpCustomizePanelProps) {
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const mergedOrder = mergeWithDefaults(sectionOrder);
  const hiddenSet = new Set(hiddenSections);

  const getLabelForSection = (id: string): string => {
    if (BUILT_IN_SECTION_LABELS[id]) return BUILT_IN_SECTION_LABELS[id];
    const custom = customSections.find((s) => s.id === id);
    return custom?.title || id;
  };

  const isCustom = (id: string): boolean => {
    return id.startsWith('custom-');
  };

  const handleAddSection = () => {
    const trimmed = newSectionTitle.trim();
    if (!trimmed) return;
    onAddCustomSection(trimmed);
    setNewSectionTitle('');
  };

  const handleStartRename = (id: string) => {
    setRenamingId(id);
    setRenameValue(getLabelForSection(id));
  };

  const handleConfirmRename = () => {
    if (renamingId && renameValue.trim()) {
      onRenameCustomSection(renamingId, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const handleDeleteCustomSection = (id: string) => {
    const section = customSections.find((s) => s.id === id);
    if (section && section.content.trim()) {
      setConfirmDeleteId(id);
    } else {
      onRemoveCustomSection(id);
    }
  };

  return (
    <div className="bg-background-card border border-accent-bronze/30 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-background-elevated">
        <span className="font-display text-sm text-accent-gold uppercase tracking-wider">Customize Layout</span>
        <button
          onClick={onClose}
          className="px-3 py-1 text-xs text-text-secondary bg-background-elevated rounded
            hover:bg-background-primary transition-colors"
        >
          Done
        </button>
      </div>

      <div className="divide-y divide-background-elevated">
        {mergedOrder.map((sectionId, idx) => {
          const isHidden = hiddenSet.has(sectionId);
          const custom = isCustom(sectionId);
          const isRenaming = renamingId === sectionId;
          const isConfirmingDelete = confirmDeleteId === sectionId;

          return (
            <div
              key={sectionId}
              className={`flex items-center gap-2 px-3 py-2 ${isHidden ? 'opacity-50' : ''}`}
            >
              {/* Move buttons */}
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => onMove(sectionId, 'up')}
                  disabled={idx === 0}
                  className="text-xs text-text-muted hover:text-text-primary disabled:opacity-30
                    disabled:cursor-not-allowed transition-colors w-5 h-4 flex items-center justify-center"
                  aria-label={`Move ${getLabelForSection(sectionId)} up`}
                >
                  ▲
                </button>
                <button
                  onClick={() => onMove(sectionId, 'down')}
                  disabled={idx === mergedOrder.length - 1}
                  className="text-xs text-text-muted hover:text-text-primary disabled:opacity-30
                    disabled:cursor-not-allowed transition-colors w-5 h-4 flex items-center justify-center"
                  aria-label={`Move ${getLabelForSection(sectionId)} down`}
                >
                  ▼
                </button>
              </div>

              {/* Label */}
              <div className="flex-1 min-w-0">
                {isRenaming ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={handleConfirmRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleConfirmRename();
                      if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                    }}
                    className="w-full bg-background-elevated border border-accent-bronze/30 rounded
                      px-2 py-0.5 text-sm text-text-primary focus:outline-none focus:border-accent-bronze"
                  />
                ) : (
                  <span className={`text-sm ${isHidden ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                    {getLabelForSection(sectionId)}
                    {custom && (
                      <span className="ml-1.5 text-[10px] text-text-muted bg-background-elevated px-1 py-0.5 rounded">
                        custom
                      </span>
                    )}
                  </span>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1">
                {/* Rename (custom only) */}
                {custom && !isRenaming && !isConfirmingDelete && (
                  <button
                    onClick={() => handleStartRename(sectionId)}
                    className="text-text-muted hover:text-text-primary transition-colors p-1"
                    aria-label={`Rename ${getLabelForSection(sectionId)}`}
                    title="Rename"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                )}

                {/* Delete confirmation */}
                {isConfirmingDelete && (
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-error">Delete?</span>
                    <button
                      onClick={() => { onRemoveCustomSection(sectionId); setConfirmDeleteId(null); }}
                      className="text-error hover:text-error/80 transition-colors p-0.5"
                      aria-label="Confirm delete"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="text-text-muted hover:text-text-primary transition-colors p-0.5"
                      aria-label="Cancel delete"
                    >
                      ✗
                    </button>
                  </div>
                )}

                {/* Delete (custom only) */}
                {custom && !isRenaming && !isConfirmingDelete && (
                  <button
                    onClick={() => handleDeleteCustomSection(sectionId)}
                    className="text-text-muted hover:text-error transition-colors p-1"
                    aria-label={`Delete ${getLabelForSection(sectionId)}`}
                    title="Delete"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}

                {/* Visibility toggle */}
                {!isConfirmingDelete && (
                  <button
                    onClick={() => onToggleVisibility(sectionId)}
                    className={`p-1 transition-colors ${isHidden ? 'text-text-muted hover:text-text-primary' : 'text-accent-gold hover:text-accent-gold-light'}`}
                    aria-label={isHidden ? `Show ${getLabelForSection(sectionId)}` : `Hide ${getLabelForSection(sectionId)}`}
                    title={isHidden ? 'Show' : 'Hide'}
                  >
                    {isHidden ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add custom section */}
      <div className="p-3 border-t border-background-elevated">
        <div className="flex gap-2">
          <input
            value={newSectionTitle}
            onChange={(e) => setNewSectionTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddSection(); }}
            placeholder="New section name..."
            className="flex-1 bg-background-elevated border border-background-elevated rounded
              px-2 py-1.5 text-sm text-text-primary
              focus:outline-none focus:border-accent-bronze/50 placeholder:text-text-muted"
          />
          <button
            onClick={handleAddSection}
            disabled={!newSectionTitle.trim()}
            className="px-3 py-1.5 text-xs font-display uppercase tracking-wider
              bg-accent-bronze/20 text-accent-gold border border-accent-bronze/30 rounded
              hover:bg-accent-bronze/30 transition-colors
              disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
